import { IApiBaseProps, ISortByQuery } from '@common/api.models';
import { createSearchAsyncFn } from '@common/search.services';
import { envConfigs } from '@config/envConfigs';
import { createDownloadFileAsyncFn, createDownloadFileWithCustomPropsAsyncFn } from '@file/file.services';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { IItemResponseBase, IListResponse } from '@gcl/react-core';
import { IRevision } from '@revision/revision.models';
import { apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { IDownloadFileBaseProps } from '@file/file.models';
import { IDesignBriefSearch, IDesignBriefSearchFilter } from './designBrief.models';
import {
  ICreateDesignBriefRequest,
  ICreateDesignBriefRevisionRequest,
  IDesignBrief,
  IGetDesignBriefRevisionsRequest,
  IUpdateDesignBriefRequest,
} from './detail/designBriefDetailPage.models';

export const apiRoutes = {
  designBrief: '/DesignBriefs',
  designBriefSearch: '/DesignBriefs/Search',
  exportDesignBriefsSearchResult: '/DesignBriefs/ExportExcel',
  exportDesignBriefsSearchResultToPdf: '/DesignBriefs/ExportPdf',
  createAndIssueToTwc: '/DesignBriefs/IssueToTwc',
  updateAndIssueToTwc: (id: number) => `/DesignBriefs/${id}/IssueToTwc`,
  updateAndIssueToTwd: (id: number) => `/DesignBriefs/${id}/IssueToTwd`,
  getRevisions: (id: number) => `/DesignBriefs/${id}/Revisions`,
  createRevision: (id: number) => `/DesignBriefs/${id}/Revisions`,
  downloadDesignBriefPdf: (id: number) => `/DesignBriefs/${id}/Pdf`,
  downloadDesignBriefProjectDiscussionsPdf: (id: number) => `/DesignBriefs/${id}/ProjectDiscussions/Pdf`,
};

export const searchDesignBriefAsync = createSearchAsyncFn<IDesignBriefSearchFilter, IDesignBriefSearch>(
  apiRoutes.designBriefSearch,
);

export interface IExportProjectDesignBriefProps extends IDownloadFileBaseProps {
  sortByQuery?: ISortByQuery;
  searchFilter: IDesignBriefSearchFilter;
}

export const exportProjectDesignBriefSearchExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectDesignBriefProps>(apiRoutes.exportDesignBriefsSearchResult);

export const exportProjectDesignBriefSearchPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportProjectDesignBriefProps>(
    apiRoutes.exportDesignBriefsSearchResultToPdf,
  );

export const createDesignBriefAsync = async (
  request: ICreateDesignBriefRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPost<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designBrief}`,
    request,
  );
  return result.data;
};

export const createAndIssueDesignBriefToTwcAsync = async (
  request: ICreateDesignBriefRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPost<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.createAndIssueToTwc}`,
    request,
  );
  return result.data;
};

export const getDesignBriefRevisionsAsync = async (
  id: number,
  request: IGetDesignBriefRevisionsRequest,
): Promise<IListResponse<IRevision>> => {
  const result = await apiServerGet<IListResponse<IRevision>>(`${envConfigs.apiBaseUrl}${apiRoutes.getRevisions(id)}`, {
    params: request,
  });
  return result.data;
};

export const createDesignBriefRevisionAsync = async (
  id: number,
  request: ICreateDesignBriefRevisionRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPost<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.createRevision(id)}`,
    request,
  );
  return result.data;
};

export const updateAndIssueDesignBriefToTwcAsync = async (
  id: number,
  request: IUpdateDesignBriefRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPut<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateAndIssueToTwc(id)}`,
    request,
  );
  return result.data;
};

export const updateAndIssueDesignBriefToTwdAsync = async (
  id: number,
  request: IUpdateDesignBriefRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPut<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateAndIssueToTwd(id)}`,
    request,
  );
  return result.data;
};

export const updateDesignBriefAsync = async (
  id: number,
  request: IUpdateDesignBriefRequest,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerPut<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designBrief}/${id}`,
    request,
  );
  return result.data;
};

export const getDesignBriefAsync = async (
  id: number,
  props: IApiBaseProps,
): Promise<IItemResponseBase<IDesignBrief>> => {
  const result = await apiServerGet<IItemResponseBase<IDesignBrief>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.designBrief}/${id}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const downloadDesignBriefPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadDesignBriefPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};

export const downloadDesignBriefProjectDiscussionsPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadDesignBriefProjectDiscussionsPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};
